import { AsyncPipe, NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { FeedbackComponent } from '@shared/components/feedback';
import { ModalComponent } from '@shared/components/modal';
import { ModalConfirmComponent } from '@shared/components/modal-confirm';
import { NavigationComponent } from '@shared/components/navigation';
import { CourseContentActions, selectCourseContent } from '@shared/store';
import { UntilDestroy } from '@shared/utils';
import { AngularSvgIconModule } from 'angular-svg-icon';
import Cookies from 'js-cookie';
import * as _ from 'lodash';
import { combineLatest, filter, firstValueFrom, map, Observable, of, Subject, Subscription, take } from 'rxjs';
import {
  AnnotationDetail,
  CourseDetailsModelV2,
  CourseReviewV2,
  CourseService,
  CourseStatusV2,
  LoaderService,
  Logger,
  ModalService,
  neededForReviewMessage,
  NotificationsService,
  ProcessingDirective,
  ProjectService,
  RouterStoreService,
  selectAnnotationHistory,
  selectCourseV2,
  TimeAgoPipe,
} from 'thkee-common';

const log = new Logger('CourseHeaderComponent');
type IntendedLearnerKeys = 'objectives' | 'requirements' | 'learners';
type DetailKeys =
  | 'title'
  | 'subtitle'
  | 'topics'
  | 'description'
  | 'skill_level'
  | 'language'
  | 'category'
  | 'subcategory'
  | 'image';
type CurriculumKeys = 'video_length' | 'lectures';
type PricingKeys = 'price';
type AnnotationKeys = 'annotations_required_unresolved_count' | 'resolved_all_required';
@UntilDestroy()
@Component({
  selector: 'app-course-header',
  templateUrl: './course-header.component.html',
  styleUrls: ['./course-header.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    NgTemplateOutlet,
    AsyncPipe,
    NgClass,
    AngularSvgIconModule,
    ProcessingDirective,
    ModalComponent,
    NavigationComponent,
    FeedbackComponent,
    NgForOf,
    ModalConfirmComponent,
    TimeAgoPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseHeaderComponent implements OnInit, OnDestroy {
  course$!: Observable<CourseDetailsModelV2>;
  annotations$!: Observable<AnnotationDetail[]>;
  courseId$!: Observable<string>;
  courseContentSubscription: Subscription = new Subscription();
  lestPath?: string;

  courseInfo$ = this.store.select(selectCourseV2);
  preview: boolean = false;
  isSavingDetails$ = this.loaderService.response('course-auto-saving');
  isSavingIntended$ = this.loaderService.response('course-intended-auto-saving');
  isLoadingDetailsV2$ = this.loaderService.response('loading-instructor-courses-detailsV2');
  courseInfoUpdateDate$ = new Subject<string>();

  constructor(
    private store: Store,
    private readonly routerStoreService: RouterStoreService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private loaderService: LoaderService,
    private courseService: CourseService,
    private readonly modalService: ModalService,
    private readonly projectService: ProjectService,
    private activatedRoute: ActivatedRoute,
    private notificationsService: NotificationsService
  ) {
    this.courseId$ = this.routerStoreService.getParam('courseId');
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.course$ = this.store.select(selectCourseV2);
    this.annotations$ = combineLatest([this.course$, this.store.select(selectAnnotationHistory)]).pipe(
      map(([course, annotations]) =>
        annotations.filter((annotation) => {
          return annotation.course === course.source;
          // return annotation.course === (typeof course.source === 'object' ? course.source?.id : course.source);
        })
      ),
      map((annotations) => {
        return annotations as AnnotationDetail[];
      })
      // tap((annotations) => log.debug('annotations', annotations))
    );

    const url = this.router.url.split('/');
    this.lestPath = _.last(url);

    this.preview = this.activatedRoute.snapshot.queryParams['preview'] === 'true';

    this.notificationsService.events$
      .pipe(
        filter(
          (event) =>
            event.type === 'resume-editing' ||
            event.type === 'view-published-version' ||
            event.type === 'withdraw-submission' ||
            event.type === 'get-last-updated-date'
        )
      )
      .subscribe((event) => {
        if (event.type === 'resume-editing') {
          this.onEdit(event.payload as CourseDetailsModelV2);
        } else if (event.type === 'view-published-version') {
          console.log('TODO: Switching version method will be call here!');
        } else if (event.type === 'withdraw-submission') {
          this.submitReviewWithdraw();
        } else if (event.type === 'get-last-updated-date') {
          this.courseInfoUpdateDate$.next(event.payload as string);
        }
      });
  }

  get canViewIssues$(): Observable<boolean> {
    return (
      this.course$?.pipe(
        map(
          (course) =>
            !!course.status &&
            [CourseStatusV2.REJECTED, CourseStatusV2.IN_REVIEW, CourseStatusV2.PUBLISHED].includes(course.status)
        )
      ) ?? of(false)
    );
  }

  // CLP preview
  previewCLP(id: string | null) {
    const queryParams: NavigationExtras = { queryParams: { preview: true, from: this.lestPath } };
    let url;
    const lang = localStorage.getItem('thk_lang') ?? Cookies.get('lang');
    if (lang === 'en-US') {
      url = this.router.serializeUrl(this.router.createUrlTree([`/en/course/${id}`], queryParams));
    } else {
      url = this.router.serializeUrl(this.router.createUrlTree([`/course/${id}`], queryParams));
    }
    window.open(url, '_blank');
  }

  // Learning lab preview
  isLoading: boolean = false;
  goToLearningLab(id: string | null) {
    if (id) {
      this.isLoading = true;
      this.store.dispatch(CourseContentActions.resetCourseOverview({ overview: {} }));
      this.store.dispatch(CourseContentActions.loadCourseOverview({ product_id: id, preview: true }));
      this.courseContentSubscription.add(
        this.store.select(selectCourseContent).subscribe({
          next: (courses) => {
            if (courses.courseOrerview.code) {
              const code = courses.courseOrerview.code;
              const section = courses.courseOrerview.last_recorded.section_chain_id;
              const subsection = courses.courseOrerview.last_recorded.subsection_chain_id;
              if (code) {
                this.isLoading = false;
                const queryParams: NavigationExtras = { queryParams: { preview: true, from: this.lestPath } };
                let url;
                const lang = localStorage.getItem('thk_lang') ?? Cookies.get('lang');
                if (lang === 'en-US') {
                  url = this.router.serializeUrl(
                    this.router.createUrlTree(
                      [`/en/learning-lab/${id}/overview/${section}/${subsection}/${code}`],
                      queryParams
                    )
                  );
                } else {
                  url = this.router.serializeUrl(
                    this.router.createUrlTree(
                      [`/learning-lab/${id}/overview/${section}/${subsection}/${code}`],
                      queryParams
                    )
                  );
                }
                window.open(url, '_blank');
                this.cdr.markForCheck();
              }
            }
          },
          error: (err) => {
            this.isLoading = false;
          },
        })
      );
    }
  }

  backToDashboard() {
    const lang = localStorage.getItem('thk_lang') ?? Cookies.get('lang');
    if (lang === 'en-US') {
      this.router.navigate(['/en/instructor/courses']);
    } else {
      this.router.navigate(['/instructor/courses']);
    }
  }

  // Toggle sidebar
  isSidebar: boolean = false;
  @Output() toggleSidebar: EventEmitter<boolean> = new EventEmitter();

  headerAction() {
    this.isSidebar = !this.isSidebar;
    this.toggleSidebar.emit(this.isSidebar);
    if (this.isSidebar) {
      this.loaderService.loaderAction('instructor-course-mobile-toggle', 'loading');
    } else {
      this.loaderService.loaderAction('instructor-course-mobile-toggle', 'success');
    }
  }

  isSearchingAnnotation: boolean = false;
  filterEvent(event: boolean) {
    this.isSearchingAnnotation = event;
  }

  // Review process
  isSubmittedForReview: boolean = false;
  project_id: string = '';
  courseReviewV2!: CourseReviewV2;
  @ViewChild('submitForReviewMessagesModal')
  submitForReviewMessagesModal!: ModalComponent;
  @ViewChild('submitForReviewConfirmModal')
  submitForReviewConfirmModal!: ModalComponent;
  private subscriptions = new Subscription();

  confirmSubmitForReview() {
    this.isSubmittedForReview = true;
    firstValueFrom(this.courseId$).then((courseId) => {
      this.courseService.courseSubmitValidationCheck(courseId).subscribe({
        next: (res) => {
          this.project_id = res.project_id;
          this.courseReviewV2 = res;
          if (!res.published_course || (res.published_course && res.has_substantial_changes)) {
            if (res.is_valid_for_review) {
              this.submitForReviewConfirmModal.open();
            } else {
              this.isSubmittedForReview = false;
              this.submitForReviewMessagesModal.open();
            }
          } else if (res.published_course && !res.has_substantial_changes) {
            this.isSubmittedForReview = false;
            this.modalService.confirm({
              title: $localize`Publish Course`,
              message: $localize`Course have no major changes and can be pulished directly. Do you want to publish this course?`,
              onConfirm: () => {
                // TODO: Once get the project_id with validation API pass it below
                this.projectService.toStateReviewOrPublished(res.project_id ?? '0');
              },
            });
          } else {
            this.isSubmittedForReview = false;
            log.error('confirmSubmitForReview', 'Invalid project state');
            this.modalService.message({
              title: $localize`Error`,
              message: $localize`Invalid project state`,
            });
          }
          this.cdr.markForCheck();
        },
      });
    });
  }

  submitForReview() {
    this.projectService.toStateInReview(this.project_id ?? '').subscribe((data) => {
      this.isSubmittedForReview = false;
      this.router.navigate([`/instructor/courses`]);
      this.modalService.message({
        title: $localize`Success`,
        message: $localize`Course submitted for review`,
      });
    });
  }

  submitReviewWithdraw() {
    this.subscriptions.add(
      this.course$?.pipe(take(1)).subscribe((course) => {
        this.modalService.confirm({
          title: $localize`Withdraw Submission`,
          message: $localize`Do you want to withdraw this submission?`,
          onConfirm: () => {
            this.submitForWithdrawConfirmed(course);
          },
        });
      })
    );
  }

  private submitForWithdrawConfirmed(course: CourseDetailsModelV2) {
    this.isSubmittedForReview = true;
    this.projectService.withdrawSubmission(course.project).subscribe((data: any) => {
      this.isSubmittedForReview = false;
      this.router.navigate([`/instructor/courses`]);
      this.modalService.message({
        title: $localize`Success`,
        message: $localize`Course review withdraw success!`,
      });
    });
  }

  onEdit(course: CourseDetailsModelV2): void {
    if (course.status === 'published') {
      this.projectService.toStateDraft(course.id);
    } else {
      const pathname = `/instructor/course/${course.id}/intended-learners`;
      window.location.replace(pathname);
    }
  }

  shouldShowCourseSubStatus(course: CourseDetailsModelV2): boolean {
    const project = course.unpublished_project;
    return !!project?.published_course && project?.published_course !== project?.unpublished_course;
  }

  get transcodingErrors(): string {
    return this.courseReviewV2?.reviews_action?.pending_lecture_transcoding
      ? neededForReviewMessage.pending_lecture_transcoding
      : '';
  }

  get blankVideoInfoErrors(): string {
    return this.courseReviewV2?.reviews_action?.blank_lecture_video_info
      ? neededForReviewMessage.blank_lecture_video_info
      : '';
  }

  get failedVideoInfoErrors(): string {
    return this.courseReviewV2?.reviews_action?.failed_lecture_video_info
      ? neededForReviewMessage.failed_lecture_video_info
      : '';
  }

  get intendedLearnersErrors(): string[] {
    return _.keys(this.courseReviewV2?.reviews_action?.intended_learners)
      .filter(
        (key): key is IntendedLearnerKeys =>
          !!this.courseReviewV2?.reviews_action?.intended_learners[key as IntendedLearnerKeys]
      )
      .map((key: IntendedLearnerKeys) => neededForReviewMessage.intended_learners[key]);
  }

  get detailsErrors(): string[] {
    return _.keys(this.courseReviewV2?.reviews_action?.details)
      .filter((key): key is DetailKeys => !!this.courseReviewV2?.reviews_action?.details[key as DetailKeys])
      .map((key) => neededForReviewMessage.details[key]);
  }

  get curriculumErrors(): string[] {
    return _.keys(this.courseReviewV2?.reviews_action?.curriculum)
      .filter((key): key is CurriculumKeys => !!this.courseReviewV2?.reviews_action?.curriculum[key as CurriculumKeys])
      .map((key) => neededForReviewMessage.curriculum[key]);
  }

  get curriculumError() {
    return this.courseReviewV2?.reviews_action?.curriculum;
  }

  get pricingErrors(): string[] {
    return _.keys(this.courseReviewV2?.reviews_action?.pricing)
      .filter((key): key is PricingKeys => !!this.courseReviewV2?.reviews_action?.pricing[key as PricingKeys])
      .map((key) => neededForReviewMessage.pricing[key]);
  }

  get annotationErrors(): string[] {
    return _.keys(this.courseReviewV2?.reviews_action?.annotations)
      .filter(
        (key): key is AnnotationKeys => this.courseReviewV2?.reviews_action?.annotations[key as AnnotationKeys] === true
      )
      .map((key) => neededForReviewMessage.annotations[key]);
  }

  get canSubmitReview$(): Observable<boolean> {
    return (
      this.course$?.pipe(
        map(
          (course) =>
            !!course.status &&
            [CourseStatusV2.DRAFT, CourseStatusV2.REJECTED, CourseStatusV2.PUBLISHED].includes(course.status)
        )
      ) ?? of(false)
    );
  }

  get canWithdrawReview$(): Observable<boolean> {
    return (
      this.course$?.pipe(map((course) => !!course.status && [CourseStatusV2.IN_REVIEW].includes(course.status))) ??
      of(false)
    );
  }

  ngOnDestroy(): void {
    this.courseContentSubscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
